/* eslint-disable @typescript-eslint/no-explicit-any */
import { Ability } from '@casl/ability';
import {
  Actions,
  AttributeDefinitionValue,
  MultiSelectOption,
  ResourceForm,
  SelectAttributeValuesVarcharsForm,
  Subjects,
} from '@dispatch/shared/types';
import {
  CheckBoxField,
  DateField,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import Close from '@mui/icons-material/Close';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import React, { useContext, useMemo } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import { ResourceContext } from '../../ResourcesDetails';
import { Add, Remove } from '@mui/icons-material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const emptyAttributeValueVarCharForm = (
  selectedAttributeDefinitionIntegrationGUID: string
): SelectAttributeValuesVarcharsForm => {
  return {
    selectedAttributeDefinitionIntegrationGUID:
      selectedAttributeDefinitionIntegrationGUID,
    attributeDefinitionValueIntegrationGUID: '',
    title: '',
    value: '',
    // Use null so DateField will show empty instead of defaulting to today
    startDate: null as unknown as string,
    endDate: null as unknown as string,
  };
};

export type AttributesFormProps = {
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  index: number;
  remove: UseFieldArrayRemove;
  watch: UseFormWatch<ResourceForm>;
  field: Record<'id', string>;
  errors: FieldErrors;
  getValues: UseFormGetValues<ResourceForm>;
  setValue: UseFormSetValue<ResourceForm>;
  isReadOnly?: boolean;
};

export function AttributesForm({
  control,
  permissions,
  index,
  remove,
  watch,
  field,
  errors,
  getValues,
  setValue,
  isReadOnly = false,
}: AttributesFormProps) {
  const { data } = useContext(ResourceContext);
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');

  const {
    fields: attributeValueFields,
    remove: attributeValueRemove,
    append: attributeValueAppend,
  } = useFieldArray({
    control,
    name: `attributes.${index}.selectedAttributeValuesVarchars`,
    keyName: 'fieldId',
  });

  const companyAttribute = data?.company?.attributeDefinition?.find(
    (attributeDef) =>
      watch(`attributes.${index}`).attributeDefinitionIntegrationGUID ===
      attributeDef.integrationGUID
  );

  const isSelectedVarChar =
    companyAttribute?.attributeDataType?.includes('string');

  const options = useMemo(() => {
    const selectedAttributeValuesVarchars = watch(
      `attributes.${index}.selectedAttributeValuesVarchars`
    );
    const filteredAttributes =
      companyAttribute?.attributeDefinitionValues.filter((companyAttr) => {
        return !selectedAttributeValuesVarchars.find(
          (selecAttr) =>
            selecAttr.attributeDefinitionValueIntegrationGUID ===
            companyAttr.integrationGUID
        );
      });
    return (
      filteredAttributes?.map((value) => {
        return {
          title: value.displayName,
          value: value.integrationGUID,
        };
      }) || []
    );
  }, [
    companyAttribute?.attributeDefinitionValues,
    watch(`attributes.${index}.selectedAttributeValuesVarchars`).map(
      (x) => x.title
    ),
  ]);

  const companyAssignmentTypes =
    data?.company?.attributeDefinition?.filter?.(
      (x: { displayName: string }) => x.displayName === 'Assignment Type'
    )?.[0]?.attributeDefinitionValues || [];
  const companyAssignmentTypeOptions = companyAssignmentTypes.map((x) => ({
    title: x.displayName,
    value: x.value,
  }));

  const disableAdd = useMemo(() => {
    const formAttributes = watch(
      `attributes.${index}.selectedAttributeValuesVarchars`
    );

    return (
      formAttributes.length ===
      companyAttribute?.attributeDefinitionValues.length
    );
  }, [
    companyAttribute?.attributeDefinitionValues.length,
    index,
    watch(`attributes.${index}.selectedAttributeValuesVarchars`),
  ]);

  const deleteAttribute = (index: number) => {
    remove(index);
    setValue('addSelectedAttributeGUID', '');
  };

  const deleteAttributeValue = (index: number) => {
    attributeValueRemove(index);
  };

  const addAttributeValue = () => {
    attributeValueAppend(
      emptyAttributeValueVarCharForm(companyAttribute?.integrationGUID || '')
    );
  };

  const onValueChange = (
    value: MultiSelectOption | (string | number)[] | null,
    valueIndex: number
  ) => {
    const option = value as MultiSelectOption;

    setValue(
      `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}`,
      {
        selectedAttributeDefinitionIntegrationGUID:
          companyAttribute?.integrationGUID || '',
        attributeDefinitionValueIntegrationGUID: option.value as string,
        title: option.title,
        value: option.value as string,
        startDate:
          getValues(
            `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.startDate`
          ) || '',
        endDate:
          getValues(
            `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.endDate`
          ) || '',
      }
    );
  };

  const [subAttributeUpdate, setSubAttributeUpdate] = React.useState<any>({});
  const company = data?.company;
  const selectedAttributeDefinitionIntegrationGUID =
    company?.attributeDefinition?.find?.(
      (x) => x.displayName === 'Assignment Type'
    )?.integrationGUID || '';
  const assignmentTypes = company?.attributeDefinition?.find(
    (c) => c.displayName === 'Assignment Type'
  )?.attributeDefinitionValues;

  const renderSubAttribute = (valueIndex: number) => {
    return (
      <SubAttribute
        errors={errors}
        valueIndex={valueIndex}
        index={index}
        control={control}
        handleAttribute={{ setSubAttributeUpdate, subAttributeUpdate }}
        companyAssignmentTypeOptions={companyAssignmentTypeOptions}
        selectedAttributeDefinitionIntegrationGUID={
          selectedAttributeDefinitionIntegrationGUID
        }
        companyAssignmentTypes={assignmentTypes}
        setValue={setValue}
        isReadOnly={isReadOnly}
      />
    );
  };

  const renderAttribute = (attributeValueField: any, valueIndex: number) => {
    const hasSubAttributes = watch(
      `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.subAttributes`
    );
    return (
      <Grid
        container
        spacing={2}
        key={attributeValueField.fieldId}
        paddingTop={3}
        size={12}
      >
        <Grid size={3}>
          <SelectField
            id={`value`}
            name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}`}
            label={companyAttribute?.displayName}
            control={control}
            readOnly={!hasUpsertPermissions}
            options={options}
            errors={
              errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                ? errors[`selectedAttributeValuesVarchars`][valueIndex]
                : null
            }
            onValueChange={(
              value: MultiSelectOption | (string | number)[] | null
            ) => onValueChange(value, valueIndex)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid size={3}>
          <DateField
            id={`startDate`}
            name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.startDate`}
            label="Start Date"
            control={control}
            errors={
              errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                ? errors[`selectedAttributeValuesVarchars`][valueIndex]
                : null
            }
            popperProps={{ sx: { zIndex: 10000 } }}
            required
            disabled={isReadOnly}
          />
        </Grid>
        <Grid size={3}>
          <DateField
            id={`endDate`}
            name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.endDate`}
            label="End Date"
            control={control}
            errors={
              errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                ? errors[`selectedAttributeValuesVarchars`][valueIndex]
                : null
            }
            popperProps={{ sx: { zIndex: 10000 } }}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid size={3}>
          {watch(`attributes.${index}.displayName`) === 'Peril' ? (
            !isReadOnly && (
              <MUIMenu
                attributeValueRemove={attributeValueRemove}
                selectedAttributeVarcharIndex={valueIndex}
                update={subAttributeUpdate[valueIndex]}
                hasSubAttribute={
                  hasSubAttributes && hasSubAttributes?.length > 0
                    ? true
                    : false
                }
              />
            )
          ) : (
            <IconButton
              disabled={isReadOnly}
              onClick={() => deleteAttributeValue(valueIndex)}
            >
              <Remove />
            </IconButton>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderAttributes = () => {
    return (
      <>
        <div
          style={{
            padding: '24px',
          }}
        >
          <Grid
            container
            spacing={4}
            key={field.id}
            sx={{ width: 'calc(100% - 32px)' }}
          >
            <Grid data-testid="attribute-name" size={6}>
              <TextField
                id={`displayName`}
                name={`attributes.${index}.displayName`}
                label="Attribute"
                type="text"
                control={control}
                inputProps={{
                  readOnly: true,
                  endAdornment: hasUpsertPermissions && (
                    <IconButton
                      disabled={isReadOnly}
                      onClick={() => deleteAttribute(index)}
                    >
                      <Close />
                    </IconButton>
                  ),
                }}
                errors={errors}
                disabled={isReadOnly}
              />
            </Grid>
            <Grid size={4}>
              <IconButton
                onClick={() => addAttributeValue()}
                disabled={disableAdd || isReadOnly}
              >
                <Add />
              </IconButton>
            </Grid>
            <Grid
              container
              data-testid="attribute-form-varchar"
              spacing={0}
              size={12}
            >
              {isSelectedVarChar &&
                attributeValueFields.map((attributeValueField, valueIndex) => {
                  return (
                    <div
                      key={`${attributeValueField.fieldId}-${valueIndex}`}
                      style={{ width: '100%' }}
                    >
                      {renderAttribute(attributeValueField, valueIndex)}
                      {renderSubAttribute(valueIndex)}
                    </div>
                  );
                })}
            </Grid>
            {!isSelectedVarChar && (
              <Grid
                container
                data-testid="attribute-form-boolean"
                spacing={0}
                size={6}
              >
                <CheckBoxField
                  id={`selectedAttributeValuesBools.value`}
                  name={`attributes.${index}.selectedAttributeValuesBools.value`}
                  label="Active"
                  disabled={!hasUpsertPermissions || isReadOnly}
                  control={control}
                />
              </Grid>
            )}
          </Grid>
        </div>
        <Divider />
      </>
    );
  };

  return <Section>{renderAttributes()}</Section>;
}

const MUIMenu = ({
  selectedAttributeVarcharIndex,
  update,
  hasSubAttribute,
  attributeValueRemove,
}: {
  selectedAttributeVarcharIndex: number;
  update: any;
  hasSubAttribute: boolean;
  attributeValueRemove: any;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div key={selectedAttributeVarcharIndex}>
      <Button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{ color: '#757575' }}
      >
        <MoreHorizIcon />
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem
          onClick={() => {
            attributeValueRemove(selectedAttributeVarcharIndex);
            handleClose();
          }}
        >
          Remove Attribute
        </MenuItem>
        <MenuItem
          // only allowed one sub attribute for now
          disabled={hasSubAttribute}
          onClick={() => {
            update({
              integrationGUID: '',
              selectedAttributeDefinitionIntegrationGUID: '',
              attributeDefinitionValueIntegrationGUID: '',
              title: '',
              value: '',
              startDate: null,
              endDate: null,
            });

            handleClose();
          }}
        >
          Add Sub Attribute
        </MenuItem>
      </Menu>
    </div>
  );
};

const SubAttribute = ({
  valueIndex,
  control,
  index,
  handleAttribute,
  errors,
  companyAssignmentTypeOptions,
  companyAssignmentTypes,
  setValue,
  selectedAttributeDefinitionIntegrationGUID,
  isReadOnly = false,
}: {
  valueIndex: number;
  control: any;
  index: number;
  handleAttribute: any;
  errors: any;
  companyAssignmentTypeOptions: any;
  companyAssignmentTypes?: AttributeDefinitionValue[];
  setValue: any;
  selectedAttributeDefinitionIntegrationGUID: string;
  isReadOnly?: boolean;
}) => {
  const {
    fields: attributeValueFields,
    remove: attributeValueRemove,
    append: attributeValueAppend,
  } = useFieldArray({
    control,
    name: `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.subAttributes`,
    keyName: 'subFieldId',
  });

  const onValueChange = (value: MultiSelectOption, id: string) => {
    const option = companyAssignmentTypes?.find((companyAssignmentType) => {
      return (
        companyAssignmentType?.value?.toUpperCase() ===
        String(value?.value)?.toUpperCase()
      );
    });

    setValue(id, {
      selectedAttributeDefinitionIntegrationGUID,
      integrationGUID: option?.integrationGUID as string,
      attributeDefinitionValueIntegrationGUID:
        option?.integrationGUID as string,
      displayName: option?.displayName,
      value: option?.value as string,
    });
  };

  React.useEffect(() => {
    const current = handleAttribute.subAttributeUpdate;

    if (attributeValueFields.length <= 0) {
      current[valueIndex] = attributeValueAppend;
      handleAttribute.setSubAttributeUpdate(current);
    }
  }, [attributeValueFields.length]);

  return (
    <>
      {attributeValueFields.map((attributeValueField, subAttributeIndex) => {
        return (
          <Stack
            key={`${attributeValueField.subFieldId}-${subAttributeIndex}`}
            direction="row"
            alignItems="flex-start"
            sx={{
              margin: 2,
              width: '100%',
            }}
            spacing={1}
          >
            <p style={{ whiteSpace: 'nowrap', margin: '1em 0' }}>Allow For:</p>
            <Grid
              container
              spacing={2}
              key={attributeValueField.subFieldId}
              sx={{
                flexBasis: 'calc(100% + 16px)',
                maxWidth: 'calc(100% + 16px)',
                pt: 2,
                pl: 2,
              }}
            >
              <Grid size={3}>
                <SelectField
                  id={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.subAttributes.${subAttributeIndex}.displayName`}
                  name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.subAttributes.${subAttributeIndex}.displayName`}
                  label={'Assignment Type'}
                  control={control}
                  options={companyAssignmentTypeOptions}
                  onValueChange={(values: any) =>
                    onValueChange(
                      values,
                      `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.subAttributes.${subAttributeIndex}`
                    )
                  }
                  error={
                    errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                      ?.subAttributes?.[subAttributeIndex]?.displayName?.message
                  }
                  errors={
                    errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                      ?.subAttributes?.[subAttributeIndex]
                      ? errors?.[`selectedAttributeValuesVarchars`]?.[
                          valueIndex
                        ]?.subAttributes?.[subAttributeIndex]
                      : null
                  }
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <DateField
                  id={`startDate`}
                  name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.subAttributes.${subAttributeIndex}.startDate`}
                  label="Start Date"
                  control={control}
                  errors={
                    errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                      ?.subAttributes?.[subAttributeIndex]
                      ? errors?.[`selectedAttributeValuesVarchars`]?.[
                          valueIndex
                        ]?.subAttributes?.[subAttributeIndex]
                      : null
                  }
                  popperProps={{ sx: { zIndex: 10000 } }}
                  required
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <DateField
                  id={`endDate`}
                  name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}.subAttributes.${subAttributeIndex}.endDate`}
                  label="End Date"
                  control={control}
                  errors={
                    errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                      ?.subAttributes?.[subAttributeIndex]
                      ? errors?.[`selectedAttributeValuesVarchars`]?.[
                          valueIndex
                        ]?.subAttributes?.[subAttributeIndex]
                      : null
                  }
                  popperProps={{ sx: { zIndex: 10000 } }}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                {!isReadOnly && (
                  <IconButton
                    onClick={() => attributeValueRemove(subAttributeIndex)}
                  >
                    <Remove />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Stack>
        );
      })}
    </>
  );
};
